// The character model: rig dimensions, the bone layout and the procedural geometry. The whole rig is
// merged into ONE geometry per team colour, every vertex rigidly weighted to one bone, so a character
// is one skinned mesh (one draw call, one shadow draw call) driven by the Bone hierarchy in character.ts.
//
// Look: chunky bevelled boxes in bold flat colour blocks. The team colour covers the vest, shoulder
// pads, upper arms, helmet cap, ear cups and backpack, plus small accents (belt buckle, wrist cuffs,
// knee pads, boot toe caps). Everything else is a mid-tone grey so limbs never vanish on dark backdrops.
// The visor is dark with a bright team-coloured glint, so the facing direction reads at range.

import * as THREE from "three";
import { MeshBuilder } from "./geom";
import type { V3 } from "./geom";
import { CHAR, shade } from "../materials/palette";
import { PART_ATTRIBUTE, PART_CLOTH, PART_GLOW, PART_VISOR } from "../materials/characterShader";

// ---------------------------------------------------------------------------------------------
// Dimensions (metres, rig space)

export const HIP_Y = 0.96;
export const THIGH = 0.44;
export const SHIN = 0.42;
export const ARM_U = 0.31;
export const ARM_L = 0.3;
export const ANKLE_H = 0.1;
export const HIP_X = 0.115;
export const SHOULDER_X = 0.3;
export const SHOULDER_Y = 0.3;

export type BoneName =
  | "hips"
  | "spine"
  | "chest"
  | "neck"
  | "head"
  | "upperArmL"
  | "lowerArmL"
  | "handL"
  | "upperArmR"
  | "lowerArmR"
  | "handR"
  | "upperLegL"
  | "lowerLegL"
  | "footL"
  | "upperLegR"
  | "lowerLegR"
  | "footR";

/** Parent bone (null: a child of the rig's `body` group) and rest offset from the parent. Parents come first. */
export const BONE_LAYOUT: Readonly<Record<BoneName, { parent: BoneName | null; pos: V3 }>> = {
  hips: { parent: null, pos: [0, HIP_Y, 0] },
  spine: { parent: "hips", pos: [0, 0.1, 0] },
  chest: { parent: "spine", pos: [0, 0.16, 0] },
  neck: { parent: "chest", pos: [0, 0.36, 0] },
  head: { parent: "neck", pos: [0, 0.02, 0] },
  upperArmR: { parent: "chest", pos: [SHOULDER_X, SHOULDER_Y, 0] },
  lowerArmR: { parent: "upperArmR", pos: [0, -ARM_U, 0] },
  handR: { parent: "lowerArmR", pos: [0, -ARM_L, 0] },
  upperArmL: { parent: "chest", pos: [-SHOULDER_X, SHOULDER_Y, 0] },
  lowerArmL: { parent: "upperArmL", pos: [0, -ARM_U, 0] },
  handL: { parent: "lowerArmL", pos: [0, -ARM_L, 0] },
  upperLegR: { parent: "hips", pos: [HIP_X, 0, 0] },
  lowerLegR: { parent: "upperLegR", pos: [0, -THIGH, 0] },
  footR: { parent: "lowerLegR", pos: [0, -SHIN, 0] },
  upperLegL: { parent: "hips", pos: [-HIP_X, 0, 0] },
  lowerLegL: { parent: "upperLegL", pos: [0, -THIGH, 0] },
  footL: { parent: "lowerLegL", pos: [0, -SHIN, 0] },
};

/** Bone order of the skeleton (the value of a vertex's skin index). */
export const BONE_NAMES = Object.keys(BONE_LAYOUT) as BoneName[];

const restPositions = new Map<BoneName, THREE.Vector3>();

function restPosition(name: BoneName): THREE.Vector3 {
  let p = restPositions.get(name);
  if (p === undefined) {
    const def = BONE_LAYOUT[name];
    p = new THREE.Vector3(def.pos[0], def.pos[1], def.pos[2]);
    if (def.parent !== null) p.add(restPosition(def.parent));
    restPositions.set(name, p);
  }
  return p;
}

/** Inverse bind matrices (rest pose = bones unrotated), shared by every skeleton. */
export const REST_INVERSES: THREE.Matrix4[] = BONE_NAMES.map((n) =>
  new THREE.Matrix4().makeTranslation(-restPosition(n).x, -restPosition(n).y, -restPosition(n).z),
);

// ---------------------------------------------------------------------------------------------
// Art

const PART_OF_KEY: Readonly<Record<string, number>> = { main: PART_CLOTH, visor: PART_VISOR, glow: PART_GLOW };

function buildBones(team: string): Record<BoneName, MeshBuilder> {
  const pack = shade(team, 0.78);
  const out = {} as Record<BoneName, MeshBuilder>;
  for (const n of BONE_NAMES) out[n] = new MeshBuilder();

  // Pelvis, belt with a team buckle, pouches
  const hips = out.hips;
  hips.box("main", [0.36, 0.17, 0.25], [0, -0.01, 0], { bevel: 0.03, color: CHAR.pants });
  hips.box("main", [0.385, 0.05, 0.27], [0, 0.07, 0], { bevel: 0.01, color: CHAR.belt });
  hips.box("main", [0.07, 0.05, 0.014], [0, 0.07, -0.138], { color: team });
  for (const s of [-1, 1]) {
    hips.box("main", [0.07, 0.11, 0.1], [s * 0.215, -0.03, 0.03], { color: CHAR.gear });
  }

  out.spine.box("main", [0.32, 0.2, 0.22], [0, 0.07, 0], { bevel: 0.02, color: CHAR.undersuit });

  // Vest in the team colour: a light chest plate, backpack, collar, shoulder pads
  const chest = out.chest;
  chest.box("main", [0.46, 0.44, 0.28], [0, 0.15, 0], { bevel: 0.05, color: team });
  chest.box("main", [0.3, 0.22, 0.035], [0, 0.17, -0.155], { bevel: 0.01, color: CHAR.plate });
  chest.box("main", [0.32, 0.3, 0.1], [0, 0.15, 0.18], { bevel: 0.03, color: pack });
  chest.box("main", [0.2, 0.18, 0.014], [0, 0.15, 0.235], { color: CHAR.gear });
  chest.box("glow", [0.2, 0.03, 0.012], [0, 0.2, 0.2415], { color: team });
  chest.box("main", [0.22, 0.05, 0.2], [0, 0.375, 0], { color: CHAR.gear });
  for (const s of [-1, 1]) {
    chest.box("main", [0.1, 0.12, 0.05], [s * 0.11, 0.04, -0.155], { color: CHAR.gear });
    chest.box("main", [0.16, 0.1, 0.22], [s * 0.3, 0.335, 0], { bevel: 0.03, color: team });
    chest.box("main", [0.1, 0.012, 0.15], [s * 0.3, 0.388, 0], { color: CHAR.plate });
  }

  out.neck.box("main", [0.12, 0.09, 0.12], [0, 0, 0], { color: CHAR.undersuit });

  // Rounded helmet: grey shell, team cap, team ear cups, dark visor with a team glint, glowing back badge
  const head = out.head;
  head.box("main", [0.3, 0.28, 0.34], [0, 0.06, 0.01], { bevel: 0.075, color: CHAR.shell });
  head.box("main", [0.304, 0.1, 0.344], [0, 0.152, 0.01], { bevel: 0.05, color: team });
  head.box("visor", [0.27, 0.1, 0.05], [0, 0.02, -0.165], { bevel: 0.01, color: CHAR.visor });
  head.box("glow", [0.22, 0.036, 0.01], [0, 0.045, -0.1915], { color: team });
  head.box("main", [0.28, 0.03, 0.06], [0, 0.085, -0.165], { color: CHAR.shellShade });
  head.box("main", [0.2, 0.08, 0.18], [0, -0.065, -0.06], { bevel: 0.01, color: CHAR.undersuit });
  head.box("glow", [0.16, 0.04, 0.012], [0, 0.06, 0.18], { color: team });
  for (const s of [-1, 1]) {
    head.box("main", [0.03, 0.12, 0.16], [s * 0.155, -0.03, -0.04], { bevel: 0.006, color: CHAR.shellShade });
    head.box("main", [0.04, 0.1, 0.1], [s * 0.17, 0.03, 0.05], { bevel: 0.01, color: team });
  }
  head.box("main", [0.02, 0.06, 0.02], [0.16, 0.12, 0.12], { color: CHAR.shellShade });

  for (const [side, tag] of [
    [1, "R"],
    [-1, "L"],
  ] as const) {
    // Team-coloured sleeve, grey armguard with a team cuff
    out[`upperArm${tag}`].box("main", [0.13, 0.31, 0.13], [0, -0.145, 0], { bevel: 0.025, color: team });
    const lower = out[`lowerArm${tag}`];
    lower.box("main", [0.14, 0.07, 0.14], [0, -0.005, 0], { bevel: 0.015, color: CHAR.plate });
    lower.box("main", [0.11, 0.28, 0.11], [0, -0.15, 0], { bevel: 0.015, color: CHAR.sleeve });
    lower.box("main", [0.12, 0.05, 0.12], [0, -0.27, 0], { color: team });
    const hand = out[`hand${tag}`];
    hand.box("main", [0.1, 0.1, 0.08], [0, -0.045, 0], { bevel: 0.015, color: CHAR.gloves });
    hand.box("main", [0.095, 0.03, 0.02], [0, -0.02, -0.04], { color: CHAR.glovesPad });
    hand.box("main", [0.03, 0.05, 0.04], [-side * 0.05, -0.03, -0.025], { bevel: 0.006, color: CHAR.gloves });

    // Mid-grey legs with a light shin plate, team knee pads and boot toe caps
    const thigh = out[`upperLeg${tag}`];
    thigh.box("main", [0.17, 0.44, 0.18], [0, -0.22, 0], { bevel: 0.03, color: CHAR.pants });
    thigh.box("main", [0.045, 0.14, 0.13], [side * 0.105, -0.15, 0], { color: CHAR.gear });
    const shin = out[`lowerLeg${tag}`];
    shin.box("main", [0.145, 0.42, 0.155], [0, -0.21, 0], { bevel: 0.025, color: shade(CHAR.pants, 0.92) });
    shin.box("main", [0.155, 0.1, 0.07], [0, 0.01, -0.09], { bevel: 0.015, color: team });
    shin.box("main", [0.12, 0.2, 0.04], [0, -0.18, -0.09], { bevel: 0.01, color: CHAR.pantsLight });
    const foot = out[`foot${tag}`];
    foot.box("main", [0.155, 0.13, 0.27], [0, -0.035, -0.065], { bevel: 0.03, color: CHAR.boots });
    foot.box("main", [0.16, 0.035, 0.285], [0, -0.0825, -0.07], { color: CHAR.sole });
    foot.box("main", [0.14, 0.07, 0.05], [0, -0.04, -0.185], { bevel: 0.02, color: team });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Merged geometry (built once per team colour, shared by every rig of that colour)

function mergeRig(bones: Record<BoneName, MeshBuilder>): THREE.BufferGeometry {
  const parts: { bone: number; part: number; geo: THREE.BufferGeometry }[] = [];
  let count = 0;
  BONE_NAMES.forEach((name, bone) => {
    for (const [key, geo] of Object.entries(bones[name].build({ vertexColors: true }))) {
      parts.push({ bone, part: PART_OF_KEY[key] ?? PART_CLOTH, geo });
      count += geo.getAttribute("position").count;
    }
  });
  const position = new Float32Array(count * 3);
  const normal = new Float32Array(count * 3);
  const color = new Float32Array(count * 3);
  const skinIndex = new Uint8Array(count * 4);
  const skinWeight = new Uint8Array(count * 4);
  const part = new Uint8Array(count);
  let at = 0;
  for (const p of parts) {
    const pos = p.geo.getAttribute("position");
    const nor = p.geo.getAttribute("normal");
    const col = p.geo.getAttribute("color");
    // Rest-pose position of the bone: the rig is bound with its bones unrotated.
    const rest = restPosition(BONE_NAMES[p.bone] as BoneName);
    for (let i = 0; i < pos.count; i++, at++) {
      position[at * 3] = pos.getX(i) + rest.x;
      position[at * 3 + 1] = pos.getY(i) + rest.y;
      position[at * 3 + 2] = pos.getZ(i) + rest.z;
      normal[at * 3] = nor.getX(i);
      normal[at * 3 + 1] = nor.getY(i);
      normal[at * 3 + 2] = nor.getZ(i);
      color[at * 3] = col.getX(i);
      color[at * 3 + 1] = col.getY(i);
      color[at * 3 + 2] = col.getZ(i);
      skinIndex[at * 4] = p.bone;
      skinWeight[at * 4] = 255;
      part[at] = p.part;
    }
    p.geo.dispose();
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(position, 3));
  geometry.setAttribute("normal", new THREE.BufferAttribute(normal, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(color, 3));
  geometry.setAttribute("skinIndex", new THREE.BufferAttribute(skinIndex, 4));
  geometry.setAttribute("skinWeight", new THREE.BufferAttribute(skinWeight, 4, true));
  geometry.setAttribute(PART_ATTRIBUTE, new THREE.BufferAttribute(part, 1));
  return geometry;
}

const geometryCache = new Map<string, THREE.BufferGeometry>();

/** The character geometry in the team colour `primary` (hex), cached. */
export function getCharacterGeometry(primary: string): THREE.BufferGeometry {
  let g = geometryCache.get(primary);
  if (g === undefined) {
    g = mergeRig(buildBones(primary));
    geometryCache.set(primary, g);
  }
  return g;
}

/** Frees the shared character geometry (all rigs must be disposed first). */
export function disposeCharacterGeometry(): void {
  for (const geo of geometryCache.values()) geo.dispose();
  geometryCache.clear();
}
